import classnames from 'classnames';
import React, { Fragment, useContext } from 'react';

import { Card } from 'base-components/Card';
import { authContext } from 'contexts/AuthContext';
import { detectNonEnglishUserLanguage } from 'pages/ProjectsPage/utils';

import Icon from '../Icon';
import { getAverageDifference, modifyTitle } from './utils';
import { IDetails, IDetailsTagType, IInfoBox } from './constants';
import styles from './InfoBoxes.module.scss';

interface Props {
  data: IInfoBox[];
  textAlign?: 'left' | 'center' | 'right';
}

const InfoBoxes: React.FC<Props> = ({ data }) => {
  const { user } = useContext(authContext);
  const isLanguageNonEnglish = detectNonEnglishUserLanguage(user.language);

  const getKpiIndicatorInfo = (row: IDetails) => {
    const diff = getAverageDifference(
      row.tag?.targetValue,
      row.tag?.compSetValue,
    );
    const isPositive = row.reverseIndicator ? diff < 0 : diff > 0;
    return {
      color: isPositive ? 'positive' : 'negative',
      icon: diff > 0 ? 'arrowKpiUp' : 'arrowKpiDown',
    };
  };

  const renderTagByType = (row: IDetails) => {
    if (!row.tag) return;

    switch (row.tag?.type) {
      case IDetailsTagType.INDICATOR: {
        if (!row.tag.indicatorValue) return;

        const iconName = getKpiIndicatorInfo(row).icon;
        const color = getKpiIndicatorInfo(row).color;
        return (
          <div className={styles['box-tag-wrapper']}>
            <Card.Tag className={classnames(styles['box-tag'], styles[color])}>
              <Icon name={iconName} height={0.8} />
              {row.tag.indicatorValue}
            </Card.Tag>
            <span className={styles['box-label']}>{row.tag?.label}</span>
          </div>
        );
      }
      case IDetailsTagType.DATE: {
        if (!row.tag.indicatorValue) return;

        return (
          <div className={styles['box-tag-wrapper']}>
            <Card.Tag
              className={classnames(styles['box-tag'], styles['box-tag-date'])}
            >
              {row.tag.indicatorValue}
            </Card.Tag>
          </div>
        );
      }
      default: {
        return null;
      }
    }
  };

  return (
    <div className={styles['boxes-list']}>
      {data?.map(({ details, title }, idx) => (
        <div key={idx} className={styles.box}>
          {title && <span className={styles['box-title']}>{title}</span>}
          {details.map(row => {
            return (
              <>
                <Fragment key={row.title}>
                  <span className={styles['box-value']}>{row.value}</span>
                  <span className={styles['box-title']}>
                    {modifyTitle(isLanguageNonEnglish, row.title)}
                  </span>
                  {row.hint && <span className={styles.hint}>{row.hint}</span>}
                </Fragment>
                {renderTagByType(row)}
              </>
            );
          })}
        </div>
      ))}
    </div>
  );
};

export type InfoBoxType = IInfoBox;
export default InfoBoxes;
